import axios from "axios";
import "../helpers/axios";
import { User, UserRole, UserLang, UserCity, UserStatus } from "../model/user";

const API_BASE_URL = process.env.REACT_APP_API_URL ?? "";

const buildUrl = (path: string): string => `${API_BASE_URL}${path}`;

export type AppUserProfile = User;

export interface AppSignupPayload {
  userID?: string;
  firstname: string;
  lastname: string;
  phoneNumber: string;
  email: string;
  city: string;
  lang: string;
}

export interface CreateTemporaryUserPayload extends AppSignupPayload {
  status?: "awaiting_validation";
}

export interface UpdateUserStatusPayload {
  email: string;
  status: UserStatus;
}

export interface UpdateUserProfilePayload extends Partial<AppSignupPayload> {}

const normalizeUserProfile = (payload: unknown): AppUserProfile => {
  const source = (payload ?? {}) as Partial<AppUserProfile>;
  return {
    userID: source.userID ?? "",
    firstname: source.firstname ?? "",
    lastname: source.lastname ?? "",
    favorites: source.favorites,
    createdAt: source.createdAt ?? "",
    email: source.email ?? "",
    phoneNumber: source.phoneNumber ?? "",
    status: (source.status ?? "awaiting_validation") as UserStatus,
    role: (source.role ?? "member") as UserRole,
    city: (source.city ?? "douala") as UserCity,
    lang: (source.lang ?? "fr") as UserLang,
  };
};

const extractProfileFromResponse = (responseData: unknown): AppUserProfile => {
  const payload = responseData as
    | { data?: AppUserProfile; user?: AppUserProfile; profile?: AppUserProfile }
    | AppUserProfile;

  if ((payload as { data?: AppUserProfile }).data) {
    return normalizeUserProfile((payload as { data: AppUserProfile }).data);
  }
  if ((payload as { user?: AppUserProfile }).user) {
    return normalizeUserProfile((payload as { user: AppUserProfile }).user);
  }
  if ((payload as { profile?: AppUserProfile }).profile) {
    return normalizeUserProfile(
      (payload as { profile: AppUserProfile }).profile,
    );
  }

  return normalizeUserProfile(payload);
};

export const createUser = async (
  payload: CreateTemporaryUserPayload,
): Promise<void> => {
  await axios.post(buildUrl("/auth/signup"), payload);
};

export const verifyUser = async (email: string): Promise<void> => {
  await axios.post(buildUrl("/auth/validate-account"), { email });
};

export const getCurrentUserProfile = async (
  userID: string,
): Promise<AppUserProfile> => {
  const response = await axios.get(buildUrl(`/users/${userID}`));
  return extractProfileFromResponse(response.data);
};

export const updateUserProfile = async (
  userID: string,
  payload: UpdateUserProfilePayload,
): Promise<AppUserProfile> => {
  const response = await axios.patch(buildUrl(`/users/${userID}`), payload);
  return extractProfileFromResponse(response.data);
};
